import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import { permissions } from '@utils/permissions';
import './Sidebar.css';

interface MenuItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
  badgeColor?: string;
  children?: MenuItem[];
  requiredPermission?: (user: any) => boolean;
}

export const Sidebar = () => {
  const { user } = useAuthStore();

  if (!user) return null;

  const menuItems: MenuItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: '🏠',
    },
    {
      path: '/customers',
      label: 'Customers',
      icon: '👥',
      requiredPermission: permissions.canViewCustomers,
    },
    {
      path: '/cases',
      label: 'Cases',
      icon: '📋',
      badge: 23, // Dynamic in future
      children: [
        { path: '/cases', label: 'All Cases', icon: '', badge: 23 },
        { path: '/cases?filter=assigned_to_me', label: 'Assigned to Me', icon: '', badge: 5 },
        { path: '/cases?filter=unassigned', label: 'Unassigned', icon: '', badge: 3, badgeColor: 'orange' },
      ],
      requiredPermission: permissions.canCreateCases,
    },
    {
      path: '/transactions',
      label: 'Transactions',
      icon: '💳',
      requiredPermission: permissions.canViewCustomers,
    },
    {
      path: '/cards',
      label: 'Cards',
      icon: '💳',
      requiredPermission: permissions.canViewCustomers,
    },
    {
      path: '/compliance',
      label: 'Compliance',
      icon: '🎯',
      requiredPermission: permissions.isComplianceOrAdmin,
    },
    {
      path: '/reports',
      label: 'Reports',
      icon: '📊',
    },
    {
      path: '/system',
      label: 'System Monitor',
      icon: '⚙️',
      requiredPermission: permissions.isOperationsOrAdmin,
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: '🔧',
    },
  ];

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    if (!item.requiredPermission) return true;
    return item.requiredPermission(user);
  });

  return (
    <aside className="sidebar">
      <nav className="sidebar-nav">
        {visibleMenuItems.map((item) => (
          <div key={item.path} className="sidebar-item-wrapper">
            <NavLink
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }: { isActive: boolean }) =>
                `sidebar-item ${isActive ? 'sidebar-item-active' : ''}`
              }
            >
              <span className="sidebar-item-icon">{item.icon}</span>
              <span className="sidebar-item-label">{item.label}</span>
              {item.badge !== undefined && (
                <span className={`sidebar-badge badge-${item.badgeColor || 'default'}`}>
                  {item.badge}
                </span>
              )}
            </NavLink>

            {item.children && item.children.length > 0 && (
              <div className="sidebar-submenu">
                {item.children.map((child) => (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    className={({ isActive }: { isActive: boolean }) =>
                      `sidebar-submenu-item ${isActive ? 'sidebar-submenu-item-active' : ''}`
                    }
                  >
                    <span className="sidebar-submenu-bullet">•</span>
                    <span className="sidebar-submenu-label">{child.label}</span>
                    {child.badge !== undefined && (
                      <span className={`sidebar-badge-small badge-${child.badgeColor || 'default'}`}>
                        {child.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
};

